// v295a-image-perf-round2
// v294b-image-components
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useLandingContent } from '../../context/LandingContentContext';
import { bgUrl } from './OptImg';

const DEFAULT_SLIDES = [
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/990d130a-8f57-4394-bde1-e4645a242480/images/d98a52e70f61764c764f9fd2d2ec013b9809c1f0dba3892c854e82f7325295d2.png',
    title: ['Dein', 'Auto.', 'Dein', 'Style.'],
  },
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/990d130a-8f57-4394-bde1-e4645a242480/images/91ab7cc43f73a54f010ee04e3775ef9a9491a32f07b0cd872aacc1cc531a3a14.png',
    title: ['Mehr', 'Power.', 'Mehr', 'Sound.'],
  },
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/990d130a-8f57-4394-bde1-e4645a242480/images/e3516934baf3e6ddc979d5cd0b2b8fa744ee094a6888b532ca0502beab682cbd.png',
    title: ['Der', 'Look.', 'Die', 'Leistung.'],
  },
];

function parseSlide(raw) {
  if (!raw) return null;
  const parts = raw.split('|');
  return parts.length === 4 ? parts : null;
}

const Hero = () => {
  const lc = useLandingContent();
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState({});

  const slides = DEFAULT_SLIDES.map((s, i) => {
    const parsed = parseSlide(lc[`hero_slide_${i + 1}`]);
    return { ...s, title: parsed || s.title };
  });

  const caption = lc.hero_caption || 'Performance Parts & Chiptuning';
  const subtitle = lc.hero_subtitle || 'Über 30.000 Premium-Teile von Maxton Design, Grail, APR, Wagner Tuning, Remus & mehr. Spoiler, Auspuffanlagen, Ladeluftkühler und professionelles Chiptuning.';
  const ctaPrimary = lc.hero_cta_primary || 'Fahrzeug konfigurieren';
  const ctaSecondary = lc.hero_cta_secondary || 'Zum Shop';
  const stats = [
    { value: lc.hero_stat_1_value || '30.000+', label: lc.hero_stat_1_label || 'Produkte' },
    { value: lc.hero_stat_2_value || '50+', label: lc.hero_stat_2_label || 'Top-Marken' },
    { value: lc.hero_stat_3_value || '1.000+', label: lc.hero_stat_3_label || 'Fahrzeuge' },
  ];

  // v295a: nur aktiven + naechsten Slide preloaden (nicht alle 3 auf einmal).
  // Reduziert initiales Payload von ~4 MB auf ~1,3 MB.
  useEffect(() => {
    const toPreload = [current, (current + 1) % slides.length];
    toPreload.forEach((i) => {
      const s = slides[i];
      if (!s || !s.image) return;
      const img = new Image();
      img.onload = () => setLoaded(p => ({ ...p, [i]: true }));
      // Sourcen durch bgUrl() -> geht ueber /api/img (WebP + Whitelist)
      img.src = bgUrl(s.image, 1920);
    });
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  const nextSlide = useCallback(() => setCurrent(c => (c + 1) % slides.length), [slides.length]);
  useEffect(() => { const t = setInterval(nextSlide, 6000); return () => clearInterval(t); }, [nextSlide]);

  const slide = slides[current];

  return (
    <section id="hero" data-testid="hero-section" style={{
      position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', background: '#030303',
      marginBottom: 0,
    }}>
      {/* Background images - crossfade with higher visibility */}
      {slides.map((s, i) => (
        <div key={i} style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${bgUrl(s.image, 1920)})`,
          backgroundSize: 'cover', backgroundPosition: 'center 30%',
          backgroundRepeat: 'no-repeat',
          opacity: i === current && loaded[i] ? 0.8 : 0,
          transition: 'opacity 1.5s ease',
          filter: 'contrast(1.1) saturate(1.1)',
        }} />
      ))}

      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.45) 100%)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '25%', background: 'linear-gradient(to top, #050505, transparent)' }} />

      {/* Red accent line */}
      <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: 0.5, duration: 1.2, ease: 'easeOut' }}
        style={{ position: 'absolute', left: '7%', top: '15%', width: '3px', height: '70%', background: 'linear-gradient(to bottom, transparent, var(--primary), transparent)', transformOrigin: 'top' }} />

      <div style={{ position: 'relative', zIndex: 10, width: '100%', paddingTop: '6rem', paddingLeft: 'max(1.5rem, calc((100vw - 1400px) / 2 + 1.5rem))' }}>
        <div style={{ maxWidth: '700px' }}>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}>
            <div className="caption" style={{ marginBottom: '1rem' }}>{caption}</div>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.h1 key={current} className="heading-display"
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              style={{ fontSize: 'clamp(3rem, 9vw, 7rem)', marginBottom: '1.5rem', lineHeight: 0.85 }}>
              <span style={{ color: 'var(--primary)' }}>{slide.title[0]}</span> {slide.title[1]}<br />
              <span style={{ WebkitTextStroke: '2px rgba(255,255,255,0.6)', color: 'transparent' }}>{slide.title[2]}</span>{' '}
              <span style={{ color: 'white' }}>{slide.title[3]}</span>
            </motion.h1>
          </AnimatePresence>

          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1 }}
            style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)', color: 'var(--fg-muted)', maxWidth: '520px', lineHeight: 1.7, marginBottom: '2.5rem' }}>
            {subtitle}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.2 }}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
            <a href="#configurator" className="btn-primary" data-testid="hero-cta-configurator">
              <span>{ctaPrimary} <ArrowRight size={16} /></span>
            </a>
            <a href="/shop" className="btn-secondary" data-testid="hero-cta-shop">{ctaSecondary}</a>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.5 }}
            style={{ marginTop: '4rem', display: 'flex', gap: '3rem', flexWrap: 'wrap' }}>
            {stats.map(stat => (
              <div key={stat.label} style={{ borderLeft: '2px solid var(--primary)', paddingLeft: '1rem' }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.75rem' }}>{stat.value}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Slide indicators */}
      <div style={{ position: 'absolute', bottom: '3rem', right: '3rem', display: 'flex', gap: '0.5rem', zIndex: 10 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => setCurrent(i)} data-testid={`hero-slide-${i}`}
            style={{ width: i === current ? '32px' : '8px', height: '4px', background: i === current ? 'var(--primary)' : 'rgba(255,255,255,0.3)', border: 'none', cursor: 'pointer', transition: 'all 0.5s' }} />
        ))}
      </div>

      <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}
        style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', color: 'var(--fg-muted)', zIndex: 10 }}>
        <ChevronDown size={24} />
      </motion.div>
    </section>
  );
};

export default Hero;
